import { Controller } from "@hotwired/stimulus"
import GoogleProvider from "../address_map/google_provider"
import LeafletProvider from "../address_map/leaflet_provider"
import { distanceKm } from "../address_map/geo"

// Lets a customer pin their exact delivery spot on a map.
//
// - Tap the map, drag the pin, search for a place (suggestions appear as they type), or use the device
//   location to set the pin.
// - The pin's address (street, area, pincode, city, state) is filled into the form, but never over
//   anything the customer typed themselves. House number and landmark stay theirs to fill in.
// - Latitude/longitude go into hidden inputs; the server saves them with the address.
//
// The map and place search come from a "provider": Google Maps + Places when a Google key is configured,
// otherwise (or if Google fails to load) OpenStreetMap via Leaflet. This controller only deals with the form.
//
// The controller element wraps the map block and the address fields, so it works for both the
// profile address form and the checkout "new address" form.

const MIN_QUERY_LENGTH = 3
const SUGGEST_DELAY_MS = 400 // pause in typing before suggestions are fetched

const LOCKABLE = ["pincode", "city", "state"]

const OUTSIDE_AREA_MESSAGE = "Sorry, this spot is outside our delivery area. Please move the pin closer to us."

export default class extends Controller {
  static targets = [
    "map", "search", "results", "status", "latitude", "longitude", "locate",
    "house", "street", "landmark", "pincode", "city", "state"
  ]

  static values = {
    searchUrl: String,
    reverseUrl: String,
    googleKey: String,
    googleMapId: String,
    hasPin: Boolean,
    confirmed: Boolean,
    lat: Number,
    lng: Number,
    centerLat: { type: Number, default: 17.385 },
    centerLng: { type: Number, default: 78.4867 },
    storeLat: Number,
    storeLng: Number,
    radiusKm: Number
  }

  connect() {
    this.pinned = this.hasPinValue && this.confirmedValue
    this.pin = this.hasPinValue ? { lat: this.latValue, lng: this.lngValue } : null
    this.dirty = new Set()
    this.locked = new Set()
    this.reverseSeq = 0
    this.searchSeq = 0
    this.searchCache = new Map()
    this.connected = true

    // Anything already filled in (editing an address) counts as typed by the customer
    this.fieldTargets().forEach(([name, el]) => {
      // (a new address starts with the state preselected, which the map may still change)
      if (el.value && (this.hasPinValue || name !== "state")) this.dirty.add(name)
      const markDirty = () => this.dirty.add(name)
      el.addEventListener("input", markDirty)
      el.addEventListener("change", markDirty)
    })

    // Clicking anywhere outside the search box, its buttons and the suggestions closes the suggestions
    this.outsideClick = (event) => {
      if (!event.target.closest("[data-address-map-search-box]")) this.hideResults()
    }
    document.addEventListener("click", this.outsideClick)

    // The map can start hidden (checkout's collapsed "new address" form); redraw when it becomes visible
    this.resizeObserver = new ResizeObserver(() => this.provider?.resize())
    this.resizeObserver.observe(this.mapTarget)

    this.submitGuard = (event) => {
      if (this.pinned || !this.isVisible()) return
      event.preventDefault()
      this.showStatus("Please select your exact location on the map first.", "error")
      this.element.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.submitGuard)

    this.ready = this.whenVisible().then(() => this.startMap())
  }

  disconnect() {
    this.connected = false
    clearTimeout(this.suggestTimer)
    this.searchAbort?.abort()
    document.removeEventListener("click", this.outsideClick)
    this.resizeObserver?.disconnect()
    this.visibilityObserver?.disconnect()
    this.form?.removeEventListener("submit", this.submitGuard)
    this.provider?.destroy()
    this.provider = null
  }

  // -- map providers --

  // Google bills per map load, and checkout keeps the "new address" form collapsed when the customer already
  // has saved addresses. So the map only starts once the form is actually on screen.
  whenVisible() {
    if (this.isVisible()) return Promise.resolve()

    return new Promise((resolve) => {
      this.visibilityObserver = new ResizeObserver(() => {
        if (!this.isVisible()) return
        this.visibilityObserver.disconnect()
        resolve()
      })
      this.visibilityObserver.observe(this.element)
    })
  }

  async startMap() {
    if (this.googleKeyValue) {
      try {
        await this.useProvider(new GoogleProvider({
          element: this.mapTarget, key: this.googleKeyValue, mapId: this.googleMapIdValue,
          onAuthFailure: () => this.fallBackToOpenStreetMap("Google Maps rejected our key")
        }))
        return
      } catch (error) {
        await this.fallBackToOpenStreetMap(error.message)
        return
      }
    }
    await this.useProvider(this.leafletProvider())
  }

  leafletProvider() {
    return new LeafletProvider({ element: this.mapTarget, searchUrl: this.searchUrlValue, reverseUrl: this.reverseUrlValue })
  }

  async useProvider(provider) {
    const start = this.pin || { lat: this.centerLatValue, lng: this.centerLngValue }
    this.provider = provider
    await provider.init(
      { center: start, zoom: this.pin ? 17 : 13, marker: this.pin },
      { onTap: (position) => this.setPin(position), onDrag: (position) => this.setPin(position) }
    )
    if (!this.connected) return provider.destroy()

    if (this.pin && !this.statusTarget.textContent.trim()) {
      this.showStatus(
        this.confirmedValue
          ? "Your saved pin. Drag it or tap the map to change it."
          : "This location is only approximate. Tap the map or drag the pin to your exact door.",
        this.confirmedValue ? "info" : "warn"
      )
    }
  }

  // Google could not start (script blocked, key rejected, billing off...): keep the form usable with the free map
  async fallBackToOpenStreetMap(reason) {
    console.warn(`Address map: ${reason}. Using OpenStreetMap instead.`)
    this.provider?.destroy()
    this.provider = null
    this.searchCache.clear()
    this.hideResults()
    if (!this.connected) return
    await this.useProvider(this.leafletProvider())
    this.showStatus("Google Maps isn't available right now, so a basic map is shown. Search and tapping the map still work.", "warn")
  }

  // -- setting the pin --

  setPin(position, { fly = false } = {}) {
    this.pin = position
    this.pinned = true
    this.provider.setMarker(position)
    this.latitudeTarget.value = position.lat.toFixed(6)
    this.longitudeTarget.value = position.lng.toFixed(6)
    if (fly) this.provider.moveTo(position, 17)
    this.showStatus("Looking up the address…", "info")
    this.reverseLookup(position)
    this.alertIfOutsideArea(position)
  }

  // A pop-up, because the inline note is easy to miss. Delayed a moment so the pin is drawn before the alert blocks the page.
  alertIfOutsideArea(position) {
    if (this.isDeliverable(position) !== false) return
    setTimeout(() => {
      if (this.pin === position) this.showOutsideAreaDialog()
    }, 100)
  }

  showOutsideAreaDialog() {
    document.getElementById("outside-area-dialog")?.remove()
    const previousFocus = document.activeElement

    const overlay = document.createElement("div")
    overlay.id = "outside-area-dialog"
    overlay.className = "fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    overlay.setAttribute("role", "alertdialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-labelledby", "outside-area-title")

    const card = document.createElement("div")
    card.className = "w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 text-center"

    const icon = document.createElement("div")
    icon.className = "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-2xl"
    icon.textContent = "📍"

    const title = document.createElement("h2")
    title.id = "outside-area-title"
    title.className = "text-lg font-bold text-gray-900"
    title.textContent = "Outside our delivery area"

    const body = document.createElement("p")
    body.className = "mt-2 text-sm text-gray-600"
    body.textContent = "Sorry, this spot is outside our delivery area. Please move the pin closer to us."

    const button = document.createElement("button")
    button.type = "button"
    button.className = "mt-5 w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
    button.textContent = "OK, I'll move the pin"

    card.append(icon, title, body, button)
    overlay.appendChild(card)

    const close = () => {
      document.removeEventListener("keydown", onKey)
      overlay.remove()
      previousFocus?.focus?.()
    }
    const onKey = (event) => { if (event.key === "Escape") close() }
    button.addEventListener("click", close)
    overlay.addEventListener("click", (event) => { if (event.target === overlay) close() })
    document.addEventListener("keydown", onKey)

    document.body.appendChild(overlay)
    button.focus()
  }

  // -- address lookups --

  async reverseLookup(position) {
    const seq = ++this.reverseSeq // ignore answers for pins that have since moved
    try {
      const result = await this.provider.reverse(position)
      if (seq !== this.reverseSeq) return
      if (!result) {
        this.showStatus("Pin set. We couldn't read an address here, so please type it in below.", "warn")
        return
      }
      this.fillFields(result)
      const deliverable = this.isDeliverable(position)
      this.showStatus(`📍 ${result.label}`, deliverable === false ? "warn" : "ok", deliverable === false ? OUTSIDE_AREA_MESSAGE : null)
    } catch (_) {
      if (seq === this.reverseSeq) this.showStatus("Pin set. We couldn't look up the address, so please type it in below.", "warn")
    }
  }

  // null when the store location isn't configured (then nothing is blocked)
  isDeliverable(position) {
    if (!this.storeLatValue || !this.storeLngValue) return null
    const radius = this.radiusKmValue || 5
    return distanceKm({ lat: this.storeLatValue, lng: this.storeLngValue }, position) <= radius
  }

  fillFields(result) {
    this.fillIfClean("street", result.street)
    LOCKABLE.forEach((name) => this.fillLockable(name, result[name]))
  }

  fillIfClean(name, value) {
    const target = this[`${name}Targets`]?.[0]
    if (!target || !value || this.dirty.has(name)) return
    // A dropdown only accepts one of its own options
    if (target.tagName === "SELECT" && ![...target.options].some((option) => option.value === value)) return
    target.value = value
  }

  // -- lock --
  // Pincode, city and state that came from the pin are locked so they can't drift away from the pinned spot; the
  // "Change" link unlocks them. A field the map has no value for stays open, as the customer has to type it.

  fillLockable(name, value) {
    const target = this[`${name}Targets`]?.[0]
    if (!target) return

    if (this.locked.has(name)) {
      if (value && this.acceptable(target, value)) target.value = value
      else this.unlockField(name, { clear: true }) // the new spot has no such value, so drop the stale one
      return
    }
    if (this.dirty.has(name) || !value || !this.acceptable(target, value)) return
    target.value = value
    this.lockField(name)
  }

  // A dropdown only accepts one of its own options
  acceptable(target, value) {
    return target.tagName !== "SELECT" || [...target.options].some((option) => option.value === value)
  }

  lockField(name) {
    const target = this[`${name}Target`]
    this.locked.add(name)
    target.classList.add("bg-gray-50", "text-gray-500", "cursor-not-allowed")
    if (target.tagName === "SELECT") {
      // a select has no readonly, and a disabled one would not be submitted
      target.tabIndex = -1
      target.addEventListener("mousedown", this.blockSelect)
      target.addEventListener("keydown", this.blockSelect)
    } else {
      target.readOnly = true
    }
    this.showLockNote()
  }

  unlockField(name, { clear = false } = {}) {
    const target = this[`${name}Target`]
    this.locked.delete(name)
    target.classList.remove("bg-gray-50", "text-gray-500", "cursor-not-allowed")
    if (target.tagName === "SELECT") {
      target.tabIndex = 0
      target.removeEventListener("mousedown", this.blockSelect)
      target.removeEventListener("keydown", this.blockSelect)
    } else {
      target.readOnly = false
    }
    if (clear) target.value = ""
    else this.dirty.add(name) // the customer chose to edit it, so the map stops overwriting it

    if (this.locked.size === 0) {
      this.lockNote?.remove()
      this.lockNote = null
    }
  }

  showLockNote() {
    if (this.lockNote) return

    const note = document.createElement("p")
    note.className = "text-xs text-gray-500"
    note.append("Pincode, city and state are filled from your pin. ")
    const change = document.createElement("button")
    change.type = "button"
    change.className = "font-semibold text-emerald-700 hover:underline"
    change.textContent = "Change"
    change.addEventListener("click", () => {
      const first = LOCKABLE.find((name) => this.locked.has(name))
      ;[...this.locked].forEach((name) => this.unlockField(name))
      if (first) this[`${first}Target`].focus()
    })
    note.appendChild(change)

    // Under the State field, the last of the three
    const anchor = this.hasStateTarget ? this.stateTarget : this.pincodeTarget
    anchor.parentElement.insertAdjacentElement("afterend", note)
    this.lockNote = note
  }

  blockSelect = (event) => {
    if (event.type === "keydown" && event.key === "Tab") return
    event.preventDefault()
  }

  // -- search suggestions --

  // Suggest places while typing. We wait for a short pause instead of searching on every keystroke,
  // which keeps the number of lookups (and the load on the map service) down.
  suggest() {
    clearTimeout(this.suggestTimer)
    if (this.searchTarget.value.trim().length < MIN_QUERY_LENGTH) {
      this.hideResults()
      return
    }
    this.suggestTimer = setTimeout(() => this.search(), SUGGEST_DELAY_MS)
  }

  async search(event) {
    if (event) event.preventDefault()
    clearTimeout(this.suggestTimer)
    const query = this.searchTarget.value.trim()
    if (query.length < MIN_QUERY_LENGTH) return

    await this.ready
    if (!this.provider) return

    const seq = ++this.searchSeq // only the latest search may update the list
    this.searchAbort?.abort()
    if (this.provider.cacheable && this.searchCache.has(query)) {
      this.renderResults(this.searchCache.get(query))
      return
    }

    if (!this.resultsTarget.hasChildNodes()) this.showResultsMessage("Searching…")
    this.searchAbort = new AbortController()
    try {
      const results = await this.provider.suggest(query, { signal: this.searchAbort.signal })
      if (seq !== this.searchSeq) return
      if (this.provider.cacheable) this.searchCache.set(query, results)
      this.renderResults(results)
    } catch (error) {
      if (error.name === "AbortError" || seq !== this.searchSeq) return
      this.showResultsMessage("Search isn't available right now. Tap the map to drop your pin instead.")
    }
  }

  // Enter searches immediately (and must not submit the whole address form); Escape closes the list
  searchKeydown(event) {
    if (event.key === "Enter") this.search(event)
    if (event.key === "Escape") this.hideResults()
  }

  hideResults() {
    clearTimeout(this.suggestTimer)
    this.searchSeq++ // discard any search still in flight
    this.searchAbort?.abort()
    this.resultsTarget.replaceChildren()
  }

  renderResults(results) {
    this.resultsTarget.replaceChildren()
    if (results.length === 0) {
      this.showResultsMessage("No places found. Try a nearby landmark or area, or tap the map.")
      return
    }
    results.forEach((result) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 border-b border-gray-100 last:border-0"
      button.textContent = result.label
      button.addEventListener("click", async () => {
        this.hideResults()
        // Keep the chosen place visible (setting it in code doesn't trigger a new search), unless it can't be delivered to
        this.searchTarget.value = result.label
        try {
          const position = await result.resolve()
          if (this.isDeliverable(position) === false) this.searchTarget.value = ""
          this.setPin(position, { fly: true })
        } catch (_) {
          this.showStatus("We couldn't open that place. Try another one or tap the map.", "warn")
        }
      })
      this.resultsTarget.appendChild(button)
    })
  }

  showResultsMessage(message) {
    const p = document.createElement("p")
    p.className = "px-4 py-3 text-sm text-gray-500"
    p.textContent = message
    this.resultsTarget.replaceChildren(p)
  }

  // -- device location --

  async locateMe() {
    if (!navigator.geolocation) {
      this.showStatus("Your browser can't share its location. Search or tap the map instead.", "warn")
      return
    }
    await this.ready
    this.locateTarget.disabled = true
    this.showStatus("Finding your location…", "info")
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.locateTarget.disabled = false
        this.setPin({ lat: position.coords.latitude, lng: position.coords.longitude }, { fly: true })
      },
      (error) => {
        this.locateTarget.disabled = false
        const denied = error.code === error.PERMISSION_DENIED
        this.showStatus(
          denied ? "Location access is blocked. Allow it in your browser, or search / tap the map instead."
                 : "Couldn't get your location. Search or tap the map instead.",
          "warn"
        )
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  // -- helpers --

  fieldTargets() {
    return ["house", "street", "landmark", "pincode", "city", "state"]
      .filter((name) => this[`has${name[0].toUpperCase()}${name.slice(1)}Target`])
      .map((name) => [name, this[`${name}Target`]])
  }

  isVisible() {
    return this.element.offsetParent !== null
  }

  showStatus(message, tone = "info", note = null) {
    const tones = {
      info: "bg-gray-50 text-gray-600 border-gray-200",
      ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
      warn: "bg-amber-50 text-amber-800 border-amber-200",
      error: "bg-rose-50 text-rose-700 border-rose-200"
    }
    this.statusTarget.className = `mt-3 px-4 py-3 rounded-xl border text-sm ${tones[tone]}`
    this.statusTarget.textContent = message
    if (note) {
      const extra = document.createElement("span")
      extra.className = "block font-semibold mt-1"
      extra.textContent = note
      this.statusTarget.appendChild(extra)
    }
  }
}
